/**
 * Verbes métier des documents de chantier.
 *
 * Tout est en N2 : déposer, partager, retirer. Rien ne sort de l'entreprise,
 * et retirer est une suppression douce — le document disparaît de toutes les
 * listes, le fichier reste jusqu'à la suppression de l'entreprise. C'est ce
 * qui le distingue d'une vraie suppression, qui serait N3.
 *
 * Les rôles sont dits ici **et** en base : ici pour refuser proprement avant
 * l'appel, en base pour que le refus tienne quel que soit le chemin.
 */
import { ActionLevel } from "../../levels.js";
import { DomainError } from "../../errors.js";
import { defineService } from "../../service.js";
import type { ExecutionContext } from "../../context.js";
import type {
  ListSiteDocumentsInput,
  RecordSiteDocumentInput,
  RemoveSiteDocumentInput,
  ShareSiteDocumentInput,
} from "./schema.js";
import { siteDocumentProblem, sortSiteDocuments } from "./types.js";
import type { SiteDocument, SiteDocumentKind } from "./types.js";

export interface RecordSiteDocumentCommand {
  projectId: string;
  storagePath: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  kind: SiteDocumentKind;
}

export interface SiteDocumentRepository {
  /** Les documents que l'appelant voit sur ce chantier — la base filtre. */
  listByProject(ctx: ExecutionContext, projectId: string): Promise<SiteDocument[]>;
  /** Rattache au chantier un fichier déjà déposé dans `chantier-documents`. */
  record(ctx: ExecutionContext, command: RecordSiteDocumentCommand): Promise<SiteDocument>;
  setShared(ctx: ExecutionContext, documentId: string, shared: boolean): Promise<void>;
  softDelete(ctx: ExecutionContext, documentId: string): Promise<void>;
}

export function createSiteDocumentServices(repo: SiteDocumentRepository) {
  const listerDocumentsChantier = defineService<ListSiteDocumentsInput, SiteDocument[]>({
    name: "lister_documents_chantier",
    level: ActionLevel.Read,
    handler: async (ctx, input) => sortSiteDocuments(await repo.listByProject(ctx, input.projectId)),
  });

  const deposerDocumentChantier = defineService<RecordSiteDocumentInput, SiteDocument>({
    name: "deposer_document_chantier",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: ["patron", "conducteur"],
    handler: (ctx, input) => {
      // Le premier segment est l'entreprise, le second le chantier : c'est la
      // convention que lisent la policy du Storage et celle de la table.
      const [tenant, project] = input.storagePath.split("/");
      if (tenant !== ctx.tenantId || project !== input.projectId) {
        throw new DomainError("invalid_input", "Ce document n'est pas rangé sous ce chantier.", {
          storagePath: input.storagePath,
        });
      }
      const problem = siteDocumentProblem({
        name: input.fileName,
        type: input.mimeType,
        size: input.sizeBytes,
      });
      if (problem) throw new DomainError("invalid_input", problem);

      return repo.record(ctx, {
        projectId: input.projectId,
        storagePath: input.storagePath,
        fileName: input.fileName.trim(),
        mimeType: input.mimeType,
        sizeBytes: input.sizeBytes,
        kind: input.kind ?? "autre",
      });
    },
  });

  const partagerDocumentTerrain = defineService<ShareSiteDocumentInput, { shared: boolean }>({
    name: "partager_document_terrain",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: ["patron", "conducteur"],
    handler: async (ctx, input) => {
      await repo.setShared(ctx, input.documentId, input.shared);
      return { shared: input.shared };
    },
  });

  const retirerDocumentChantier = defineService<RemoveSiteDocumentInput, { removed: true }>({
    name: "retirer_document_chantier",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: ["patron"],
    handler: async (ctx, input) => {
      await repo.softDelete(ctx, input.documentId);
      return { removed: true };
    },
  });

  return {
    listerDocumentsChantier,
    deposerDocumentChantier,
    partagerDocumentTerrain,
    retirerDocumentChantier,
  };
}

export type SiteDocumentServices = ReturnType<typeof createSiteDocumentServices>;
